import { NavLink, useParams, useNavigate } from "react-router";
import React, { useEffect } from "react";
import { Form, Input, Typography, App } from "antd";
import { Error as CommonError } from "@triare/auth-redux/dist/saga/common";
import { Action } from "@triare/auth-redux";
import { connect } from "react-redux";
import {
  resetPassword as resetPasswordAction,
  resetPasswordErrorClear as resetPasswordErrorClearAction,
  ResetPasswordAction,
  ResetPasswordResponse,
} from "@triare/auth-redux/dist/saga/auth/resetPassword";
import {
  validationNewPassword,
  createRulesForAntd,
} from "../../../../utils/validations";
import { getMessageInError } from "../../../../hooks/fetch";
import Wrapper from "../Wrapper";
import Submit from "../../../Common/Form/Button/Submit";
import { RootState } from "../../../../store";
import { moduleName } from "../../../../store/auth";
import { useTranslation } from "react-i18next";

import styles from "./index.module.scss";

const validationRules = createRulesForAntd(validationNewPassword);

interface ResetPasswordProps {
  loading: boolean;
  response: ResetPasswordResponse | null;
  error: CommonError;
  resetPassword: (payload: ResetPasswordAction) => Action;
  resetPasswordErrorClear: () => Action;
}

function ResetPassword({
  resetPassword,
  resetPasswordErrorClear,
  loading,
  response,
  error,
}: ResetPasswordProps) {
  const { secretKey } = useParams();
  const { message } = App.useApp();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const onFinish = ({ password }: { password: string }) => {
    if (secretKey) {
      resetPassword({ password, secretKey });
    }
  };

  useEffect(() => {
    if (response) {
      message.info("Your password has been successfully changed");
      navigate("/sign-in");
    }
  }, [response]);

  // The same channel the success above already uses, and the one the rest of
  // the app uses for a failed request. Cleared once shown, as useMessageError
  // does, so a stale error cannot surface when the screen is opened again.
  useEffect(() => {
    if (error) {
      message.error(getMessageInError(error));
      resetPasswordErrorClear();
    }
  }, [error]);

  return (
    <Wrapper>
      <Form
        className={styles.form}
        layout="vertical"
        scrollToFirstError
        onFinish={onFinish}
      >
        <Typography.Title level={3} className={styles.title}>
          Set and confirm new password
        </Typography.Title>

        <Form.Item
          name="password"
          required
          label={t("newPassword")}
          className={styles.input}
          rules={[validationRules]}
        >
          <Input.Password placeholder={t("newPassword")} size="large" />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          required
          label={t("confirmNewPassword")}
          className={styles.input}
          dependencies={["password"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }

                return Promise.reject(
                  new Error(t("validations.passwordsDoNotMatch")),
                  // "Passwords that you entered do not match!"
                );
              },
            }),
          ]}
        >
          <Input.Password placeholder="Confirm new password" size="large" />
        </Form.Item>

        <Submit loading={loading} size="large">
          {t("signIn.text")}
        </Submit>
      </Form>

      <Typography.Text
        strong
        style={{ textAlign: "center", display: "block", width: "100%" }}
      >
        {t("common.dontHaveAccount")} &thinsp;
        <NavLink to="/sign-up">{t("signUp.text")}</NavLink>
      </Typography.Text>
    </Wrapper>
  );
}

export default connect(
  (state: RootState) => ({
    loading: state[moduleName].resetPassword.loading,
    response: state[moduleName].resetPassword.response,
    error: state[moduleName].resetPassword.error,
  }),
  {
    resetPassword: resetPasswordAction,
    resetPasswordErrorClear: resetPasswordErrorClearAction,
  },
)(ResetPassword);
